/**
 * "This server needs a sign-in": what the MCP tab shows when a server answered 401 / 403, instead of a red line. Each
 * option that applies (vfs-service src/mcptester/signIn.js, in the order to try them) is a button explained in one
 * line; picking one opens what it needs right there:
 *
 * - Sign in with OAuth: Flow's backend finds where to sign in (protected resource metadata → authorization server →
 *   registration or a client id → PKCE), the sign-in opens in a popup and comes back to mcp-oauth-callback.html; the
 *   token is used for this browser session.
 * - Use my Flow sign-in: the person's own Flow access token is sent (the server trusts Flow's issuer).
 * - The variable the code checks (MCP_TOKEN…): editors generate a random token, save it as the service's secret and use
 *   it (the service reads it when it restarts: Restart is offered); a signing secret is explained, with Use a token.
 * - An app user: one of the app's accounts.
 * - Use a token: header and value inline, kept for this browser session only (never in the project), with Save as a
 *   secret for later (editors).
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Key, LockKey, SignIn, UserCircle } from '@phosphor-icons/react';
import { setSecret } from '../../services/AppConfigService';
import { finishMcpOAuth, startMcpOAuth } from '../../services/McpTesterService';
import { errorText } from '../data/DataCommon';
import { userLabel } from '../../utils/apiTesterModel';
import { OAUTH_MESSAGE, generateToken, headerValue, oauthRedirectUri } from '../../utils/mcpTesterModel';

const ICONS = { oauth: SignIn, flow: SignIn, secret: LockKey, user: UserCircle, token: Key };
const VARIABLE = /^[A-Z_][A-Z0-9_]*$/;

const keyOf = (option) => `${option.kind}${option.variable ? `:${option.variable}` : ''}`;

export default function McpSignInPanel({
  guide, projectId, service, serviceName, path, canWrite, users = [], token, onUse, onRestart = undefined, restarting = false,
}) {
  const options = guide?.options || [];
  const [open, setOpen] = useState(() => (options[0] ? keyOf(options[0]) : null));
  const tokenOption = options.find((o) => o.kind === 'token') || { header: 'Authorization', scheme: 'Bearer ' };
  const [draft, setDraft] = useState(() => ({ name: token?.value ? token.name : tokenOption.header, value: token?.value || '' }));
  const [userId, setUserId] = useState(() => (users.find((u) => !u.disabled)?.id ?? ''));
  const [oauth, setOauth] = useState({ busy: false, error: null, needsClient: null, clientId: '', link: null });
  const [secret, setSecretState] = useState({ busy: false, error: null, saved: null, name: options.find((o) => o.kind === 'secret' && o.mode === 'shared')?.variable || 'MCP_TOKEN' });
  const listener = useRef(null);

  const channel = useRef(null);
  const stopListening = () => {
    if (listener.current) { window.removeEventListener('message', listener.current); }
    listener.current = null;
    if (channel.current) { channel.current.close(); }
    channel.current = null;
  };
  useEffect(() => stopListening, []);
  useEffect(() => { if (userId === '' && users.length) { setUserId(users.find((u) => !u.disabled)?.id ?? ''); } }, [users, userId]);

  if (!options.length) { return null; }

  const connectWithDraft = () => {
    if (!draft.value.trim()) { return; }
    onUse({ kind: 'token', name: draft.name.trim() || 'Authorization', value: headerValue(draft.name.trim().toLowerCase() === 'authorization' ? tokenOption.scheme || 'Bearer ' : '', draft.value), source: 'manual' });
  };

  /** Generates a token, saves it as the service's secret and uses it (the service reads it after a restart). */
  async function generateAndSave(option) {
    setSecretState((s) => ({ ...s, busy: true, error: null }));
    try {
      const value = generateToken();
      await setSecret(projectId, service, option.variable, value);
      setSecretState((s) => ({ ...s, busy: false, saved: option.variable }));
      onUse({ kind: 'token', name: option.header || 'Authorization', value: headerValue(option.scheme ?? 'Bearer ', value), source: 'secret' }, { connect: false });
    } catch (error) {
      setSecretState((s) => ({ ...s, busy: false, error: errorText(error) }));
    }
  }

  /** "Save as a secret for later": the token the person typed, as one of the service's secrets. */
  async function saveDraftAsSecret() {
    const name = secret.name.trim();
    if (!VARIABLE.test(name)) { setSecretState((s) => ({ ...s, error: 'A secret name is capital letters, digits and _' })); return; }
    setSecretState((s) => ({ ...s, busy: true, error: null }));
    try {
      const raw = draft.value.trim().replace(/^Bearer\s+/i, '');
      await setSecret(projectId, service, name, raw);
      setSecretState((s) => ({ ...s, busy: false, saved: name }));
    } catch (error) {
      setSecretState((s) => ({ ...s, busy: false, error: errorText(error) }));
    }
  }

  async function signInWithOAuth(option) {
    // Opened now, in the click, so that the browser does not block it; pointed at the sign-in once Flow has it.
    const popup = window.open('', 'flow-mcp-oauth', 'width=520,height=700');
    setOauth((o) => ({ ...o, busy: true, error: null, link: null }));
    try {
      const started = await startMcpOAuth(projectId, {
        service, path, redirectUri: oauthRedirectUri(), resourceMetadata: option.resourceMetadata || undefined, ...(oauth.clientId.trim() ? { clientId: oauth.clientId.trim() } : {}),
      });
      if (popup && !popup.closed) { popup.location.href = started.authorizationUrl; } else { setOauth((o) => ({ ...o, link: started.authorizationUrl })); }
      stopListening();
      const onMessage = async(event) => {
        if (event.origin !== window.location.origin || event.data?.type !== OAUTH_MESSAGE || event.data.state !== started.state) { return; }
        stopListening();
        if (event.data.error || !event.data.code) {
          setOauth((o) => ({ ...o, busy: false, error: `The sign-in did not complete: ${event.data.errorDescription || event.data.error || 'no code came back'}` }));
          return;
        }
        try {
          const got = await finishMcpOAuth(projectId, { state: started.state, code: event.data.code });
          setOauth((o) => ({ ...o, busy: false, link: null }));
          onUse({ kind: 'token', name: 'Authorization', value: `${got.tokenType && /^bearer$/i.test(got.tokenType) ? 'Bearer' : got.tokenType || 'Bearer'} ${got.accessToken}`, source: 'oauth' });
        } catch (error) {
          setOauth((o) => ({ ...o, busy: false, error: errorText(error) }));
        }
      };
      listener.current = onMessage;
      window.addEventListener('message', onMessage);
      // When the sign-in page cut the popup off from this tab, the callback page says it on a channel instead.
      if (typeof BroadcastChannel === 'function') {
        channel.current = new BroadcastChannel(OAUTH_MESSAGE);
        channel.current.onmessage = (event) => onMessage({ origin: window.location.origin, data: event.data });
      }
    } catch (error) {
      if (popup && !popup.closed) { popup.close(); }
      setOauth((o) => ({ ...o, busy: false, error: error.code === 'oauth_client_needed' ? null : errorText(error), needsClient: error.code === 'oauth_client_needed' ? errorText(error) : o.needsClient }));
    }
  }

  const tokenFields = (
    <div className="mcp-signin-fields" data-testid="mcp-signin-token-fields">
      <label className="data-sr" htmlFor="mcp-signin-header">Header</label>
      <input className="data-input data-mono mcp-signin-header" id="mcp-signin-header" onChange={(event) => setDraft((d) => ({ ...d, name: event.target.value }))} placeholder="Authorization" value={draft.name} />
      <label className="data-sr" htmlFor="mcp-signin-value">Token</label>
      <input autoComplete="off" className="data-input data-mono mcp-signin-value" id="mcp-signin-value" onChange={(event) => setDraft((d) => ({ ...d, value: event.target.value }))} onKeyDown={(event) => { if (event.key === 'Enter') { connectWithDraft(); } }} placeholder={draft.name.trim().toLowerCase() === 'authorization' ? 'The token (Bearer is added)' : 'The value'} type="password" value={draft.value} />
      <button className="data-btn is-primary" data-testid="mcp-signin-use-token" disabled={!draft.value.trim()} onClick={connectWithDraft} type="button">Connect</button>
      {canWrite && (
        <span className="mcp-signin-save">
          <label className="data-sr" htmlFor="mcp-signin-secret-name">Secret name</label>
          <input className="data-input data-mono" id="mcp-signin-secret-name" onChange={(event) => setSecretState((s) => ({ ...s, name: event.target.value }))} value={secret.name} />
          <button className="data-btn" data-testid="mcp-signin-save-secret" disabled={!draft.value.trim() || secret.busy} onClick={saveDraftAsSecret} type="button">Save as a secret of {serviceName}</button>
        </span>
      )}
    </div>
  );

  const detail = (option) => {
    switch (option.kind) {
    case 'oauth':
      return (
        <div className="mcp-signin-fields">
          {oauth.needsClient && (
            <>
              <p className="data-muted mcp-signin-note">{oauth.needsClient}</p>
              <label className="data-sr" htmlFor="mcp-signin-client">Client id</label>
              <input className="data-input data-mono" id="mcp-signin-client" onChange={(event) => setOauth((o) => ({ ...o, clientId: event.target.value }))} placeholder="Client id" value={oauth.clientId} />
            </>
          )}
          <button className="data-btn is-primary" data-testid="mcp-signin-oauth-go" disabled={oauth.busy || (oauth.needsClient && !oauth.clientId.trim())} onClick={() => signInWithOAuth(option)} type="button">
            {oauth.busy ? 'Waiting for the sign-in…' : 'Sign in'}
          </button>
          {oauth.link && <a className="data-link" href={oauth.link} rel="noreferrer" target="_blank">The popup was blocked: open the sign-in page</a>}
          {oauth.error && <span className="data-error-text" role="alert">{oauth.error}</span>}
        </div>
      );
    case 'flow':
      return (
        <div className="mcp-signin-fields">
          <button className="data-btn is-primary" data-testid="mcp-signin-flow-go" onClick={() => onUse({ kind: 'flow' })} type="button">Connect with my Flow sign-in</button>
          <span className="data-muted mcp-signin-note">Only for services you trust: they receive your own Flow access token.</span>
        </div>
      );
    case 'user':
      return (
        <div className="mcp-signin-fields">
          {users.length ? (
            <>
              <label className="data-sr" htmlFor="mcp-signin-user">App user</label>
              <select className="data-input" id="mcp-signin-user" onChange={(event) => setUserId(event.target.value)} value={userId}>
                {users.map((user) => <option disabled={user.disabled} key={user.id} value={user.id}>{userLabel(user)}</option>)}
              </select>
              <button className="data-btn is-primary" data-testid="mcp-signin-user-go" disabled={userId === ''} onClick={() => onUse({ kind: 'user', id: userId })} type="button">Connect as this user</button>
            </>
          ) : <span className="data-muted mcp-signin-note">The app has no accounts yet: create a test user in the API tab.</span>}
        </div>
      );
    case 'secret':
      if (option.mode !== 'shared') {
        return (
          <div className="mcp-signin-fields">
            <span className="data-muted mcp-signin-note">Have a token the app signed? Paste it under Use a token.</span>
            <button className="data-btn" onClick={() => setOpen('token')} type="button">Use a token</button>
          </div>
        );
      }
      return (
        <div className="mcp-signin-fields">
          {canWrite ? (
            <>
              <button className="data-btn is-primary" data-testid="mcp-signin-generate" disabled={secret.busy} onClick={() => generateAndSave(option)} type="button">
                {secret.busy ? 'Saving…' : `Generate a token, save it as ${option.variable} and use it`}
              </button>
              <button className="data-btn" onClick={() => setOpen('token')} type="button">I have its value</button>
            </>
          ) : <span className="data-muted mcp-signin-note">Ask an editor of the project to set {option.variable}, then paste its value under Use a token.</span>}
          {secret.saved === option.variable && (
            <span className="mcp-signin-saved" data-testid="mcp-signin-restart" role="status">
              Saved as {option.variable}. {serviceName} reads it when it starts: restart it, then Connect.
              {onRestart && <button className="data-btn" disabled={restarting} onClick={onRestart} type="button">{restarting ? 'Restarting…' : `Restart ${serviceName} and connect`}</button>}
            </span>
          )}
          {secret.error && <span className="data-error-text" role="alert">{secret.error}</span>}
        </div>
      );
    default:
      return (
        <>
          {tokenFields}
          <span className="data-muted mcp-signin-note">Kept for this browser session only and sent with each call; never stored in the project.</span>
          {secret.saved && secret.saved === secret.name.trim() && <span className="mcp-signin-saved" role="status">Saved as {secret.saved} in Config › Secrets of {serviceName} (it reads it after a restart).</span>}
          {secret.error && <span className="data-error-text" role="alert">{secret.error}</span>}
        </>
      );
    }
  };

  return (
    <section aria-label="This server needs a sign-in" className="mcp-signin data-full" data-testid="mcp-signin">
      <strong className="mcp-signin-title">This server needs a sign-in</strong>
      <p className="data-muted mcp-signin-intro">{serviceName} {path} answered {guide.status || 401}. Pick how to sign in:</p>
      <ul className="mcp-signin-options">
        {options.map((option) => {
          const key = keyOf(option);
          const Icon = ICONS[option.kind] || Key;
          const on = open === key;
          return (
            <li className={`mcp-signin-option${on ? ' is-on' : ''}`} key={key}>
              <button aria-expanded={on} className="data-btn mcp-signin-pick" data-testid={`mcp-signin-${option.kind}`} onClick={() => setOpen(key)} type="button">
                <Icon aria-hidden size={15} />
                {option.title}
              </button>
              <span className="mcp-signin-line">{option.line}</span>
              {on && <div className="mcp-signin-detail">{detail(option)}</div>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

McpSignInPanel.propTypes = {
  canWrite: PropTypes.bool,
  guide: PropTypes.shape({ status: PropTypes.number, options: PropTypes.arrayOf(PropTypes.object) }),
  onRestart: PropTypes.func,
  onUse: PropTypes.func.isRequired,
  path: PropTypes.string,
  projectId: PropTypes.string.isRequired,
  restarting: PropTypes.bool,
  service: PropTypes.string.isRequired,
  serviceName: PropTypes.string,
  token: PropTypes.shape({ name: PropTypes.string, value: PropTypes.string }),
  users: PropTypes.arrayOf(PropTypes.object),
};
